/** Escapes HTML so orchestrator content can't inject markup. */
export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Renders a safe subset of Markdown for chat bubbles:
 * bold, italic, inline code, links, paragraphs, and pipe tables.
 */
export function renderRichText(text: string): string {
  const escaped = escapeHtml(text);
  const lines = escaped.split('\n');
  const parts: string[] = [];
  let tableRows: string[] = [];
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    if (!paragraphLines.length) {
      return;
    }
    const joined = paragraphLines.join('<br>');
    parts.push(`<p>${formatInlineMarkdown(joined)}</p>`);
    paragraphLines = [];
  };

  const flushTable = () => {
    if (tableRows.length < 2) {
      paragraphLines.push(...tableRows);
      tableRows = [];
      return;
    }

    flushParagraph();
    const body = tableRows
      .filter((line) => !line.match(/^\|[\s-|]+\|$/))
      .map((line) => {
        const cells = line
          .split('|')
          .slice(1, -1)
          .map((cell) => `<td>${formatInlineMarkdown(cell.trim())}</td>`)
          .join('');
        return `<tr>${cells}</tr>`;
      })
      .join('');
    parts.push(`<table class="domain-table"><tbody>${body}</tbody></table>`);
    tableRows = [];
  };

  for (const line of lines) {
    if (line.trim().startsWith('|')) {
      flushParagraph();
      tableRows.push(line);
      continue;
    }

    flushTable();
    if (!line.trim()) {
      flushParagraph();
      continue;
    }
    paragraphLines.push(line);
  }

  flushTable();
  flushParagraph();
  return parts.join('') || `<p>${formatInlineMarkdown(escaped)}</p>`;
}

function formatInlineMarkdown(value: string): string {
  const placeholders: string[] = [];
  const protect = (html: string): string => {
    const index = placeholders.length;
    placeholders.push(html);
    return `\u0000${index}\u0000`;
  };

  let output = value;

  // Inline code first so markup inside backticks is left alone.
  output = output.replace(/`([^`]+)`/g, (_match, code: string) =>
    protect(`<code>${code}</code>`),
  );

  // Links: [label](https://...)
  output = output.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
    (_match, label: string, href: string) =>
      protect(
        `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`,
      ),
  );

  // Bold before italic so ** wins over *
  output = output.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  output = output.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  output = output.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  output = output.replace(/_([^_]+)_/g, '<em>$1</em>');

  return output.replace(/\u0000(\d+)\u0000/g, (_match, index: string) => {
    return placeholders[Number(index)] ?? '';
  });
}
